import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { DocumentApi } from "~/functions/api/document";
import { ProcessLockApi } from "~/functions/api/processLock";
import type { PluginProposalData } from "~/interface/plugin";
import type { RootState } from "~/store";

interface PluginProposalState {
  data: PluginProposalData[];
  copyData: PluginProposalData[];
  isEditable: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: PluginProposalState = {
  data: [],
  copyData: [],
  isEditable: false,
  isLoading: true,
  error: null,
};

// APIインスタンスの作成
const documentApi = new DocumentApi();
const processLockApi = new ProcessLockApi();

// データのソートとフィルタリング
const sortAndFilterData = (data: PluginProposalData[]) => {
  const groupedData = data.reduce(
    (acc, item) => {
      const key = item.必要機能;
      if (!acc[key]) acc[key] = [];
      acc[key]!.push(item);
      return acc;
    },
    {} as Record<string, PluginProposalData[]>,
  );

  return Object.values(groupedData).flatMap((group) => {
    // group.sort((a, b) => parseFloat(b.マッチ度) - parseFloat(a.マッチ度));
    return group.map((item, index) => ({
      ...item,
      必要機能: index === 0 ? item.必要機能 : "",
    }));
  });
};

// プラグイン提案書データの取得
export const fetchPluginProposal = createAsyncThunk(
  "pluginProposal/fetch",
  async (documentIdArg: string | undefined, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;

      // URLパラメータから取得したIDを優先、なければReduxストアから取得
      const documentId = documentIdArg || currentProject.outputs?.["plugin-proposal"]?.id;

      if (!documentId) {
        return rejectWithValue("プラグイン提案書が見つかりません");
      }

      const response = await documentApi.get(
        currentUser.organization_id,
        currentProject.id,
        documentId,
      );

      if (response === null) {
        return rejectWithValue("プラグイン提案書の取得に失敗しました");
      }

      const rawPluginProposal = response.content;
      const parsedPluginProposal: any = JSON.parse(rawPluginProposal);
      const pluginProposalRows: [] = parsedPluginProposal.rows;

      const formattedData = pluginProposalRows.map((row: any) => ({
        fid: row[0] || "",
        必要機能: row[1] || "",
        プラグイン名: row[2] || "",
        機能一覧: row[3] || "",
        価格情報: row[4] || "",
        会社名: row[5] || "",
        提案理由: row[6] || "",
        マッチ度: row[7] || "",
        URL: row[8] || "",
      }));

      return sortAndFilterData(formattedData);
    } catch (_error) {
      return rejectWithValue("データの取得中にエラーが発生しました");
    }
  },
);

// ロックの取得
export const acquireLock = createAsyncThunk(
  "pluginProposal/acquireLock",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;

      if (!currentProject.outputs?.["plugin-proposal"]?.id) {
        return rejectWithValue("プラグイン提案書が見つかりません");
      }

      const response = await processLockApi.lock(currentUser.organization_id, currentProject.id, {
        type: "plugin-proposal",
        mode: "edit",
      });

      if (response === null) {
        return rejectWithValue("編集権限を取得できませんでした");
      }

      return true;
    } catch (_error) {
      return rejectWithValue("ロックの取得中にエラーが発生しました");
    }
  },
);

// ロックの解除
export const releaseLock = createAsyncThunk(
  "pluginProposal/releaseLock",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;

      if (!currentProject.outputs?.["plugin-proposal"]?.id) {
        return rejectWithValue("プラグイン提案書が見つかりません");
      }

      await processLockApi.unlock(currentUser.organization_id, currentProject.id, {
        type: "plugin-proposal",
        mode: "edit",
      });

      return true;
    } catch (_error) {
      return rejectWithValue("ロックの解除中にエラーが発生しました");
    }
  },
);

// データの更新
export const updatePluginProposal = createAsyncThunk(
  "pluginProposal/update",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;
      const { data } = state.pluginProposal;

      if (!currentProject.outputs?.["plugin-proposal"]?.id) {
        return rejectWithValue("プラグイン提案書が見つかりません");
      }

      const headers = [
        "fid",
        "必要機能",
        "プラグイン名",
        "機能一覧",
        "価格情報",
        "会社名",
        "提案理由",
        "マッチ度",
        "URL",
      ];
      const rows = data.map((row) =>
        headers.map((header) => row[header as keyof PluginProposalData] || ""),
      );

      const updateData = {
        content: JSON.stringify({
          headers,
          rows,
        }),
      };

      const response = await documentApi.updateContent(
        currentUser.organization_id,
        currentProject.id,
        currentProject.outputs?.["plugin-proposal"]?.id,
        updateData,
      );

      if (response === null) {
        return rejectWithValue("データの更新に失敗しました");
      }

      // データの更新が成功したら、ロックを解除
      await dispatch(releaseLock());

      return true;
    } catch (_error) {
      return rejectWithValue("データの更新中にエラーが発生しました");
    }
  },
);

const pluginProposalSlice = createSlice({
  name: "pluginProposal",
  initialState,
  reducers: {
    setData: (state, action: PayloadAction<PluginProposalData[]>) => {
      state.data = action.payload;
    },
    setCopyData: (state, action: PayloadAction<PluginProposalData[]>) => {
      state.copyData = action.payload;
    },
    restoreFromCopyData: (state) => {
      state.data = JSON.parse(JSON.stringify(state.copyData));
    },
    updateRow: (state, action: PayloadAction<{ index: number; data: PluginProposalData }>) => {
      state.data[action.payload.index] = action.payload.data;
    },
    addRow: (state, action: PayloadAction<PluginProposalData>) => {
      state.data.push(action.payload);
    },
    deleteRow: (state, action: PayloadAction<number>) => {
      state.data = state.data.filter((_, index) => index !== action.payload);
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchPluginProposal
      .addCase(fetchPluginProposal.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPluginProposal.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
        state.copyData = JSON.parse(JSON.stringify(action.payload));
      })
      .addCase(fetchPluginProposal.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // acquireLock
      .addCase(acquireLock.fulfilled, (state) => {
        state.isEditable = true;
        state.copyData = JSON.parse(JSON.stringify(state.data));
      })
      .addCase(acquireLock.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // releaseLock
      .addCase(releaseLock.fulfilled, (state) => {
        state.isEditable = false;
      })
      .addCase(releaseLock.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // updatePluginProposal
      .addCase(updatePluginProposal.fulfilled, (state) => {
        state.isEditable = false;
      })
      .addCase(updatePluginProposal.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  setData,
  setCopyData,
  restoreFromCopyData,
  updateRow,
  addRow,
  deleteRow,
  clearError,
} = pluginProposalSlice.actions;

export default pluginProposalSlice.reducer;
